<template>
    <v-container class="px-5">
        <v-row align="center" class="mb-1">
            <v-col cols="auto">
                <h4>Clean Truck Check - Submission Readiness</h4>
                <div class="text-caption text-medium-emphasis">
                    Platform configuration required to package and transmit to CARB CTC-VIS.
                </div>
            </v-col>
            <v-spacer/>
            <v-col cols="auto">
                <v-btn variant="text" prepend-icon="mdi-file-document-multiple-outline"
                       :to="{ name: 'admin-ctc-submissions' }">
                    Submissions
                </v-btn>
            </v-col>
        </v-row>

        <v-skeleton-loader v-if="loading" type="card"/>

        <template v-else>
            <v-alert :type="ready ? 'success' : 'warning'" variant="tonal" class="mb-4" border="start">
                {{ ready
                    ? 'Ready to submit: encryption key and CTC-VIS transport are configured.'
                    : 'Configuration incomplete - submissions will stay queued until the items below are set.' }}
            </v-alert>

            <v-row>
                <v-col cols="12" md="7">
                    <v-card rounded="lg" variant="outlined">
                        <v-card-text>
                            <div v-for="item in checks" :key="item.key"
                                 class="d-flex align-center justify-space-between py-2 border-b">
                                <div>
                                    <div class="text-body-2 font-weight-medium">{{ item.label }}</div>
                                    <div class="text-caption text-medium-emphasis">{{ item.hint }}</div>
                                </div>
                                <v-chip size="small" variant="tonal"
                                        :color="item.ok ? 'success' : 'error'"
                                        :prepend-icon="item.ok ? 'mdi-check-circle' : 'mdi-close-circle'">
                                    {{ item.ok ? 'Configured' : 'Missing' }}
                                </v-chip>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>

                <v-col cols="12" md="5">
                    <v-card rounded="lg" variant="outlined">
                        <v-card-text>
                            <div class="d-flex justify-space-between py-2 border-b">
                                <span class="text-medium-emphasis">Transport mode</span>
                                <v-chip size="small" variant="tonal" :color="modeColor">{{ data.transport_mode || '-' }}</v-chip>
                            </div>
                            <div class="d-flex justify-space-between py-2 border-b">
                                <span class="text-medium-emphasis">Transport driver</span>
                                <span class="font-weight-medium">{{ data.transport_driver || '-' }}</span>
                            </div>
                            <div class="d-flex justify-space-between py-2">
                                <span class="text-medium-emphasis">Vendor code</span>
                                <span class="font-weight-medium">{{ data.vendor_code || '-' }}</span>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </template>
    </v-container>
</template>

<script setup>
import {computed, onMounted, ref} from 'vue';
import {useRouter} from 'vue-router';
import {send} from '@/composables/apiClient';
import {useSnackbarStore} from '@/stores/snackBarStore';
import {useAuthStore} from '@/stores/authStore';

const router = useRouter();
const {showSnackbar} = useSnackbarStore();
const authStore = useAuthStore();

const loading = ref(true);
const data = ref({});

const checks = computed(() => [
    {key: 'gpg', label: 'GPG encryption key', hint: 'Public key used to encrypt the submission package', ok: !!data.value.gpg_key_configured},
    {key: 'endpoint', label: 'CTC-VIS endpoint', hint: 'CARB upload destination', ok: !!data.value.ctc_vis_endpoint_configured},
    {key: 'creds', label: 'CTC-VIS credentials', hint: 'Upload authentication', ok: !!data.value.ctc_vis_credentials_configured},
    {key: 'alert', label: 'Failure alert email', hint: 'Where submission failures are reported', ok: !!data.value.alert_email_configured},
]);

const ready = computed(() =>
    !!data.value.gpg_key_configured
    && !!data.value.ctc_vis_endpoint_configured
    && data.value.transport_mode === 'production',
);

const modeColor = computed(() => {
    const m = data.value.transport_mode;
    if (m === 'production') return 'success';
    if (m === 'sandbox') return 'info';
    return 'grey';
});

const load = () => {
    loading.value = true;
    send('get', 'admin/ctc/readiness')
        .then((res) => {
            data.value = res.data.data || {};
        })
        .catch(() => showSnackbar('Readiness could not be loaded', 'error'))
        .finally(() => loading.value = false);
};

onMounted(() => {
    if (!authStore.user?.super) {
        showSnackbar('Super admin access required', 'error');
        router.replace({name: 'dashboard'});
        return;
    }
    load();
});
</script>
